/* ============================================================
   BuilderCanvas — Framer-class drag-and-drop grid canvas
   ─────────────────────────────────────────────────────────
   • Dot-grid background with CSS radial-gradient
   • Floating glass-morphism toolbar on selected blocks
   • Animated drop zones with pulse effect
   • Column overlay guides while dragging
   • Smooth drag ghost with glow + scale
   • Artboard with device-responsive width
   • Corner resize handles on selected blocks
   • Professional empty state with onboarding
   ============================================================ */

import { useState, useCallback, useRef, useLayoutEffect, type DragEvent, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react'
import { getCardDef, type CanvasBlock } from '@/lib/cms/card-registry'
import CardRenderer from './cards/CardRenderer'
import BlockContextMenu from './BlockContextMenu'

// ── Types ─────────────────────────────────────────────

// Re-export DeviceMode from canonical location for backward compatibility
export type { DeviceMode } from '@/pages/admin/cms-editor/types'
import type { DeviceMode } from '@/pages/admin/cms-editor/types'

export interface CanvasProps {
  blocks: CanvasBlock[]
  selectedBlockId: string | null
  selectedBlockIds?: Set<string>
  zoom: number
  deviceMode?: DeviceMode
  showGrid?: boolean
  lockedBlockIds?: Set<string>
  onSelectBlock: (blockId: string | null, shiftKey?: boolean) => void
  onReorder: (blocks: CanvasBlock[]) => void
  onAddBlock: (type: string, insertIndex?: number) => void
  onRemoveBlock: (blockId: string) => void
  onDuplicateBlock: (blockId: string) => void
  onResizeBlock: (blockId: string, colSpan: number) => void
  onUpdateBlock: (blockId: string, data: Record<string, string>) => void
  onToggleBlockLock?: (blockId: string) => void
}

const DEVICE_WIDTHS: Record<DeviceMode, number> = {
  desktop: 1440,
  tablet: 768,
  mobile: 375,
}

/** Map forge template categories to canvas block types */
const FORGE_CATEGORY_TO_BLOCK: Record<string, string> = {
  hero: 'hero-banner',
  pricing: 'service-card',
  staff: 'team-card',
  stylist: 'stylist-flip-card',
  booking: 'cta-block',
  footer: 'divider',
}

/** Extract a block type from any supported drag data format */
function extractBlockType(dataTransfer: DataTransfer): string | null {
  // Standard sidebar drag
  const cardData = dataTransfer.getData('application/x-card')
  if (cardData) {
    try { return JSON.parse(cardData).type } catch { /* ignore */ }
  }
  // Forge template drag
  const forgeData = dataTransfer.getData('application/x-forge')
  if (forgeData) {
    try {
      const { category } = JSON.parse(forgeData)
      return FORGE_CATEGORY_TO_BLOCK[category?.toLowerCase()] || 'cta-block'
    } catch { /* ignore */ }
  }
  return null
}

/** Check if a drag event carries a supported card/forge type */
function hasCardDragType(dataTransfer: DataTransfer): boolean {
  return dataTransfer.types.includes('application/x-card') ||
         dataTransfer.types.includes('application/x-forge')
}

// ── Row Drop Zone (full-width separator between row groups) ───

function RowDropZone({ index, isActive, onDrop, onReorderDrop }: {
  index: number
  isActive: boolean
  onDrop: (type: string, index: number) => void
  onReorderDrop: (fromIndex: number, toIndex: number) => void
}) {
  const [isOver, setIsOver] = useState(false)

  return (
    <div
      className={`col-span-12 transition-all duration-300 ease-out relative ${
        isActive ? 'py-4 -my-2 z-40' : 'py-0 z-0'
      }`}
      onDragOver={(e) => {
        if (hasCardDragType(e.dataTransfer) || e.dataTransfer.types.includes('application/x-reorder')) {
          e.preventDefault()
          e.stopPropagation()
          e.dataTransfer.dropEffect = e.dataTransfer.types.includes('application/x-reorder') ? 'move' : 'copy'
          setIsOver(true)
        }
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsOver(false)

        // Handle block reorder
        const reorderData = e.dataTransfer.getData('application/x-reorder')
        if (reorderData) {
          const fromIdx = parseInt(reorderData, 10)
          if (!isNaN(fromIdx)) {
            onReorderDrop(fromIdx, index)
          }
          return
        }

        // Handle new card or forge template drop
        const blockType = extractBlockType(e.dataTransfer)
        if (blockType) {
          onDrop(blockType, index)
        }
      }}
    >
      <div className={`
        mx-3 rounded-full transition-all duration-300 ease-out
        ${isOver
          ? 'h-2 bg-[var(--editor-accent,#3b82f6)] shadow-[0_0_24px_rgba(59,130,246,0.5)] scale-100'
          : isActive ? 'h-[2px] bg-white/[0.1] hover:bg-white/[0.2] scale-100' : 'h-0 scale-95'
        }
      `} />
      
      <div className={`flex justify-center transition-all duration-300 pointer-events-none ${
        isOver ? 'opacity-100 -mt-3.5 relative z-30 translate-y-0' : 'opacity-0 h-0 overflow-hidden translate-y-2'
      }`}>
        <span className="text-white text-[10px] font-bold px-4 py-1 rounded-full uppercase tracking-wider scale-100 shadow-xl" style={{ background: 'var(--editor-accent, #3b82f6)' }}>
          Drop Block Here
        </span>
      </div>
    </div>
  )
}

// ── Helper: group consecutive blocks into rows that fit 12 cols ──

function groupBlocksIntoRows(blocks: CanvasBlock[]): { row: CanvasBlock[]; startIndex: number }[] {
  const rows: { row: CanvasBlock[]; startIndex: number }[] = []
  let currentRow: CanvasBlock[] = []
  let currentSpan = 0
  let startIndex = 0

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]
    // If this block starts a new row (12-col full width, or doesn't fit)
    if (block.colSpan >= 12 || (currentSpan + block.colSpan > 12 && currentRow.length > 0)) {
      // Flush current row
      if (currentRow.length > 0) {
        rows.push({ row: currentRow, startIndex })
      }
      currentRow = [block]
      currentSpan = block.colSpan
      startIndex = i
    } else {
      if (currentRow.length === 0) startIndex = i
      currentRow.push(block)
      currentSpan += block.colSpan
    }
  }
  // Flush remaining
  if (currentRow.length > 0) {
    rows.push({ row: currentRow, startIndex })
  }
  return rows
}

// ── Block Wrapper with Selection + Toolbar + Edge Resize ──

function BlockWrapper({
  block,
  isSelected,
  isMultiSelected,
  isLocked,
  onSelect,
  onRemove,
  onDuplicate,
  onResize,
  onUpdateData,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onMoveUp,
  onMoveDown,
  onToggleLock,
  artboardWidth,
}: {
  block: CanvasBlock
  index: number
  isSelected: boolean
  isMultiSelected?: boolean
  isLocked?: boolean
  onSelect: (shiftKey?: boolean) => void
  onRemove: () => void
  onDuplicate: () => void
  onResize: (colSpan: number) => void
  onUpdateData: (data: Record<string, string>) => void
  onContextMenu: (e: ReactMouseEvent) => void
  onDragStart: (e: DragEvent) => void
  onDragEnd: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  onToggleLock?: () => void
  artboardWidth: number
}) {
  const [isHovered, setIsHovered] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [resizePreview, setResizePreview] = useState<number | null>(null)
  const resizeStartRef = useRef<{ startX: number; startSpan: number } | null>(null)
  const def = getCardDef(block.type)

  // ── Edge-drag resize logic ──────────────────────
  const handleResizeStart = useCallback((e: ReactMouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsResizing(true)
    resizeStartRef.current = { startX: e.clientX, startSpan: block.colSpan }

    const gridGap = 16 // gap-4 = 16px
    const padding = 48 // p-6 = 24px * 2
    const totalGap = 11 * gridGap // 11 gaps between 12 columns
    const colWidth = (artboardWidth - padding - totalGap) / 12

    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizeStartRef.current) return
      const delta = ev.clientX - resizeStartRef.current.startX
      const colDelta = Math.round(delta / (colWidth + gridGap))
      const rawSpan = Math.max(1, Math.min(12, resizeStartRef.current.startSpan + colDelta))

      // Snap to nearest allowed col span
      const allowed = def?.allowedColSpans || [3, 4, 6, 12]
      const snapped = allowed.reduce((best, s) =>
        Math.abs(s - rawSpan) < Math.abs(best - rawSpan) ? s : best
      , allowed[0])
      setResizePreview(snapped)
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      setIsResizing(false)
      if (resizePreview !== null && resizePreview !== block.colSpan) {
        onResize(resizePreview)
      }
      setResizePreview(null)
      resizeStartRef.current = null
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [block.colSpan, artboardWidth, def, onResize, resizePreview])

  // Animated selection border (marching ants effect via CSS)
  const selectionStyle: CSSProperties = isSelected ? {
    outline: isMultiSelected
      ? '2px dashed rgba(160,130,255,0.6)'
      : '2px solid var(--editor-accent, #3b82f6)',
    outlineOffset: '3px',
    borderRadius: '14px',
  } : {}

  return (
    <div
      className={`
        relative group transition-[opacity,transform,background-color,border-color,filter,width] duration-200 ease-out
        ${isSelected ? 'z-20' : isHovered ? 'z-10' : 'z-0'}
      `}
      style={{
        gridColumn: `span ${block.colSpan} / span ${block.colSpan}`,
        ...selectionStyle,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => { e.stopPropagation(); onSelect(e.shiftKey) }}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onSelect(); onContextMenu(e) }}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      {/* ── Hover glow effect ── */}
      <div className={`
        absolute -inset-1 rounded-xl pointer-events-none transition-[opacity,transform,background-color,border-color,filter,width] duration-300 ease-out
        ${isSelected
          ? 'bg-[var(--editor-accent,#3b82f6)]/[0.04]'
          : isHovered
            ? 'bg-white/[0.01]'
            : ''
        }
      `} />

      {/* ── Floating Toolbar (glass) ── */}
      {(isSelected || isHovered) && (
        <div
          className={`
            absolute -top-10 left-1/2 -translate-x-1/2 z-40
            flex items-center gap-0.5 px-1.5 py-1 rounded-xl
            bg-[#111118]/90 backdrop-blur-2xl
            border border-white/[0.08]
            shadow-[0_8px_32px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)]
            transition-[opacity,transform,background-color,border-color,filter,width] duration-200 ease-out
            ${isSelected ? 'opacity-100 translate-y-0' : 'opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0'}
          `}
        >
          {/* Block type label */}
          <div className={`
            flex items-center gap-1.5 px-2.5 py-1 rounded-lg mr-1 text-[10px] font-semibold tracking-wide
            ${isSelected
              ? 'text-[var(--editor-accent,#3b82f6)]'
              : 'text-white/50'
            }
          `} style={isSelected ? { background: 'color-mix(in srgb, var(--editor-accent, #3b82f6) 15%, transparent)' } : undefined}>
            <span className="text-sm">{def?.icon || '📄'}</span>
            <span className="uppercase">{def?.label || block.type}</span>
          </div>

          {/* Divider */}
          <div className="w-px h-5 bg-white/[0.08]" />

          {/* Column span pills */}
          {def && def.allowedColSpans.length > 1 && (
            <>
              <div className="flex items-center gap-px ml-1">
                {def.allowedColSpans.map((span) => (
                  <button
                    key={span}
                    onClick={(e) => { e.stopPropagation(); onResize(span) }}
                    className={`
                      w-7 h-6 rounded-md text-[9px] font-mono font-bold transition-[opacity,transform,background-color,border-color,filter,width] duration-150
                      ${block.colSpan === span
                        ? 'text-[var(--editor-accent,#3b82f6)]'
                        : 'text-white/25 hover:text-white/60 hover:bg-white/[0.06]'
                      }
                    `}
                    title={`${span} columns`}
                  >
                    {span}
                  </button>
                ))}
              </div>
              <div className="w-px h-5 bg-white/[0.08]" />
            </>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-px">
            {/* Move Up */}
            {onMoveUp && (
              <button
                aria-label="Move up (⌘↑)"
                onClick={(e) => { e.stopPropagation(); onMoveUp() }}
                className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-[opacity,transform,background-color,border-color,filter,width]"
              >
                <svg aria-hidden="true" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                </svg>
              </button>
            )}

            {/* Move Down */}
            {onMoveDown && (
              <button
                aria-label="Move down (⌘↓)"
                onClick={(e) => { e.stopPropagation(); onMoveDown() }}
                className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-[opacity,transform,background-color,border-color,filter,width]"
              >
                <svg aria-hidden="true" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}

            <div className="w-px h-4 bg-white/[0.06]" />

            {/* Drag handle */}
            <button
              aria-label="Drag to reorder"
              className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/[0.06] cursor-grab active:cursor-grabbing transition-[opacity,transform,background-color,border-color,filter,width]"
            >
              <svg aria-hidden="true" className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
                <circle cx="5" cy="3" r="1"/><circle cx="11" cy="3" r="1"/>
                <circle cx="5" cy="8" r="1"/><circle cx="11" cy="8" r="1"/>
                <circle cx="5" cy="13" r="1"/><circle cx="11" cy="13" r="1"/>
              </svg>
            </button>

            {/* Lock toggle */}
            {onToggleLock && (
              <button
                aria-label={isLocked ? 'Unlock block (⌘L)' : 'Lock block (⌘L)'}
                onClick={(e) => { e.stopPropagation(); onToggleLock() }}
                className={`p-1.5 rounded-lg transition-[opacity,transform,background-color,border-color,filter,width] ${
                  isLocked
                    ? 'text-amber-400/70 hover:text-amber-300 hover:bg-amber-500/10'
                    : 'text-white/30 hover:text-white/70 hover:bg-white/[0.06]'
                }`}
              >
                <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  {isLocked ? (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  )}
                </svg>
              </button>
            )}

            {/* Duplicate */}
            <button
              aria-label="Duplicate block (⌘D)"
              onClick={(e) => { e.stopPropagation(); onDuplicate() }}
              className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-[opacity,transform,background-color,border-color,filter,width]"
            >
              <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>

            {/* Delete */}
            <button
              aria-label="Delete block (⌘⌫)"
              onClick={(e) => { e.stopPropagation(); onRemove() }}
              className="p-1.5 rounded-lg text-white/30 hover:text-rose-400 hover:bg-rose-500/10 transition-[opacity,transform,background-color,border-color,filter,width]"
            >
              <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Corner resize dots (Framer-style) ── */}
      {isSelected && (
        <>
          <div className="absolute -top-[3px] -left-[3px] w-[7px] h-[7px] rounded-full border-2 border-[#111118] z-30 pointer-events-none" style={{ background: 'var(--editor-accent, #3b82f6)' }} />
          <div className="absolute -top-[3px] -right-[3px] w-[7px] h-[7px] rounded-full border-2 border-[#111118] z-30 pointer-events-none" style={{ background: 'var(--editor-accent, #3b82f6)' }} />
          <div className="absolute -bottom-[3px] -left-[3px] w-[7px] h-[7px] rounded-full border-2 border-[#111118] z-30 pointer-events-none" style={{ background: 'var(--editor-accent, #3b82f6)' }} />
          <div className="absolute -bottom-[3px] -right-[3px] w-[7px] h-[7px] rounded-full border-2 border-[#111118] z-30 pointer-events-none" style={{ background: 'var(--editor-accent, #3b82f6)' }} />
        </>
      )}

      {/* ── Edge-drag resize handles ── */}
      {(isSelected || isHovered) && (
        <>
          {/* Right edge */}
          <div
            onMouseDown={handleResizeStart}
            className={`
              absolute top-2 -right-1 bottom-2 w-2 z-30 cursor-col-resize
              flex items-center justify-center
              transition-[opacity,transform,background-color,border-color,filter,width] duration-150
              ${isResizing
                ? 'bg-blue-500/20'
                : 'hover:bg-blue-500/10 group-hover:opacity-100'
              }
              ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
            `}
          >
            <div className={`w-[3px] h-8 rounded-full transition-[opacity,transform,background-color,border-color,filter,width] duration-150 ${
              isResizing ? 'bg-blue-500/80 scale-y-110' : 'bg-white/15 group-hover:bg-blue-500/40'
            }`} />
          </div>
        </>
      )}

      {/* ── Resize preview badge ── */}
      {isResizing && resizePreview !== null && (
        <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 z-50 px-2.5 py-1 rounded-md text-[10px] font-mono font-bold text-white shadow-[0_2px_12px_rgba(59,130,246,0.4)]" style={{ background: 'var(--editor-accent, #3b82f6)' }}>
          {resizePreview}/12
        </div>
      )}

      {/* ── Card content ── */}
      <div className={`transition-[opacity,transform,background-color,border-color,filter,width] duration-200 ${isHovered && !isSelected ? 'brightness-110' : ''}`}>
        <CardRenderer
          block={block}
          blockStyles={block.styles}
          editing={{
            onUpdateData,
            isSelected,
          }}
        />
      </div>
    </div>
  )
}

// ── Empty Canvas — Onboarding ─────────────────────────

function EmptyCanvas() {
  return (
    <div className="col-span-12 flex flex-col items-center justify-center py-40 text-center">
      {/* Animated icon */}
      <div className="relative mb-8">
        <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-500/10 to-transparent border border-dashed border-blue-500/20 flex items-center justify-center">
          <svg className="w-10 h-10 text-blue-500/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
          </svg>
        </div>
        {/* Pulse ring */}
        <div className="absolute inset-0 rounded-2xl border border-blue-500/10 animate-ping" style={{ animationDuration: '3s' }} />
      </div>

      <h3 className="text-lg font-semibold text-white/50 mb-3 tracking-tight">
        Your canvas is ready
      </h3>
      <p className="text-sm text-white/20 max-w-md leading-relaxed mb-8">
        Drag cards from the sidebar to compose your page.
        <br />
        Each card is an independent building block you can arrange and resize.
      </p>

      {/* Quick-start hints */}
      <div className="flex gap-3">
        {[
          { icon: '🎯', label: 'Hero Banner', tip: '12 cols' },
          { icon: '✂️', label: 'Service Cards', tip: '4 cols each' },
          { icon: '⭐', label: 'Testimonials', tip: '4 cols each' },
        ].map((hint) => (
          <div
            key={hint.label}
            className="px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.05] text-center"
          >
            <span className="text-lg mb-1 block">{hint.icon}</span>
            <span className="text-[10px] text-white/30 font-medium block">{hint.label}</span>
            <span className="text-[9px] text-white/15 font-mono">{hint.tip}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Column Grid Overlay ───────────────────────────────

function ColumnOverlay({ showGuides }: { showGuides?: boolean }) {
  return (
    <div className="absolute inset-0 pointer-events-none z-[5]">
      {/* 12-column grid */}
      <div className="absolute inset-0 grid grid-cols-12 gap-4 px-6">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="bg-blue-500/[0.02] border-x border-blue-500/[0.04] h-full"
          />
        ))}
      </div>

      {/* Alignment guides — shown during drag */}
      {showGuides && (
        <>
          {/* Center vertical */}
          <div className="absolute top-0 bottom-0 left-1/2 w-px border-l border-dashed border-[#ff6b6b]/30 -translate-x-px" />
          {/* Third lines */}
          <div className="absolute top-0 bottom-0 left-1/3 w-px border-l border-dashed border-[#ffd93d]/20" />
          <div className="absolute top-0 bottom-0 left-2/3 w-px border-l border-dashed border-[#ffd93d]/20" />
          {/* Quarter lines */}
          <div className="absolute top-0 bottom-0 left-1/4 w-px border-l border-dotted border-[#6bcaff]/15" />
          <div className="absolute top-0 bottom-0 left-3/4 w-px border-l border-dotted border-[#6bcaff]/15" />
        </>
      )}
    </div>
  )
}

// ── Canvas Ruler ──────────────────────────────────────

function CanvasRuler({ width }: { width: number }) {
  const ticks = Math.ceil(width / 100)
  return (
    <div className="h-5 bg-[#0d0d1a] border-b border-white/[0.04] flex items-end overflow-hidden relative">
      {Array.from({ length: ticks + 1 }).map((_, i) => (
        <div
          key={i}
          className="absolute bottom-0 flex flex-col items-center"
          style={{ left: `${(i * 100 / width) * 100}%` }}
        >
          <span className="text-[7px] text-white/15 font-mono mb-0.5">
            {i * 100}
          </span>
          <div className="w-px h-1.5 bg-white/10" />
        </div>
      ))}
    </div>
  )
}

// ── Main Canvas ───────────────────────────────────────

export default function BuilderCanvas({
  blocks,
  selectedBlockId,
  selectedBlockIds,
  zoom,
  deviceMode = 'desktop',
  showGrid = false,
  lockedBlockIds,
  onSelectBlock,
  onReorder,
  onAddBlock,
  onRemoveBlock,
  onDuplicateBlock,
  onResizeBlock,
  onUpdateBlock,
  onToggleBlockLock,
}: CanvasProps) {
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [activeDrop, setActiveDrop] = useState(false)
  const canvasRef = useRef<HTMLDivElement>(null)
  const artboardRef = useRef<HTMLDivElement>(null)
  const [artboardHeight, setArtboardHeight] = useState(0)
  const artboardWidth = DEVICE_WIDTHS[deviceMode]

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; blockId: string; blockIdx: number } | null>(null)

  const handleBlockContextMenu = useCallback((e: ReactMouseEvent, blockId: string, blockIdx: number) => {
    setContextMenu({ x: e.clientX, y: e.clientY, blockId, blockIdx })
  }, [])

  const handleMoveBlock = useCallback((fromIdx: number, direction: 'up' | 'down') => {
    const toIdx = direction === 'up' ? fromIdx - 1 : fromIdx + 1
    if (toIdx < 0 || toIdx >= blocks.length) return
    const newBlocks = [...blocks]
    const [moved] = newBlocks.splice(fromIdx, 1)
    newBlocks.splice(toIdx, 0, moved)
    onReorder(newBlocks)
  }, [blocks, onReorder])

  // Measure the artboard's actual height and update the wrapper so the
  // scroll container knows the correct scaled height.
  useLayoutEffect(() => {
    const el = artboardRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setArtboardHeight(entry.contentRect.height + 120) // +120 for ruler + header + padding
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // ── Drag handlers ──────────────────────────

  const handleDragStart = useCallback((e: DragEvent, idx: number) => {
    setDragIdx(idx)
    e.dataTransfer.setData('application/x-reorder', String(idx))
    e.dataTransfer.effectAllowed = 'move'
    // Create drag ghost with glow
    requestAnimationFrame(() => {
      const el = e.currentTarget as HTMLElement
      if (el) {
        el.style.opacity = '0.4'
        el.style.transform = 'scale(0.95)'
        el.style.filter = 'grayscale(0.5)'
      }
    })
  }, [])

  const handleDragEnd = useCallback(() => {
    setDragIdx(null)
    if (canvasRef.current) {
      canvasRef.current.querySelectorAll('[draggable]').forEach((el) => {
        ;(el as HTMLElement).style.opacity = '1'
        ;(el as HTMLElement).style.transform = ''
      })
    }
  }, [])

  const handleInsertDrop = useCallback((type: string, insertIndex: number) => {
    onAddBlock(type, insertIndex)
  }, [onAddBlock])

  const handleReorderDrop = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex === toIndex - 1) return
    const newBlocks = [...blocks]
    const [moved] = newBlocks.splice(fromIndex, 1)
    // Adjust insertion index after removal
    const adjustedTo = toIndex > fromIndex ? toIndex - 1 : toIndex
    newBlocks.splice(adjustedTo, 0, moved)
    onReorder(newBlocks)
  }, [blocks, onReorder])

  const handleCanvasDragOver = useCallback((e: DragEvent) => {
    if (hasCardDragType(e.dataTransfer) || e.dataTransfer.types.includes('application/x-reorder')) {
      e.preventDefault()
      setActiveDrop(true)
    }
  }, [])

  const handleCanvasDragLeave = useCallback(() => setActiveDrop(false), [])
  const handleCanvasClick = useCallback(() => onSelectBlock(null), [onSelectBlock])

  const handleEmptyDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const blockType = extractBlockType(e.dataTransfer)
    if (blockType) {
      onAddBlock(blockType, 0)
    }
  }, [onAddBlock])

  // ── Dot grid background ────────────────────
  const dotGridStyle: CSSProperties = {
    backgroundImage: `
      radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)
    `,
    backgroundSize: '20px 20px',
    backgroundPosition: '10px 10px',
  }

  // The scaled height is the artboard's full size * zoom. We set this on
  // a wrapper div so the scroll container can report an accurate scrollHeight.
  const scaledHeight = artboardHeight > 0 ? artboardHeight * zoom : undefined

  return (
    <div
      ref={canvasRef}
      className="flex-1 overflow-auto relative"
      style={{
        backgroundColor: '#08080f',
        ...dotGridStyle,
      }}
      onDragOver={handleCanvasDragOver}
      onDragLeave={handleCanvasDragLeave}
      onClick={handleCanvasClick}
    >
      {/* Scaled-height wrapper — collapses the dead space left by CSS scale */}
      <div
        style={{
          width: '100%',
          height: scaledHeight ? `${scaledHeight}px` : undefined,
          paddingBottom: '2rem', // clear the status bar
        }}
      >
        {/* ── Canvas viewport with zoom ────────── */}
        <div
          ref={artboardRef}
          className="mx-auto transition-[opacity,transform,background-color,border-color,filter,width] duration-300 ease-out pt-6 pb-24"
          style={{
            width: `${artboardWidth}px`,
            transform: `scale(${zoom})`,
            transformOrigin: 'top center',
          }}
        >
        {/* ── Artboard header ── */}
        <div className="flex items-center justify-between px-1 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500/30" />
            <span className="text-[10px] text-white/20 font-mono tracking-wider uppercase">
              {deviceMode} — {artboardWidth}px
            </span>
          </div>
          <span className="text-[9px] text-white/10 font-mono">
            {blocks.length} block{blocks.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* ── Ruler ── */}
        <CanvasRuler width={artboardWidth} />

        {/* ── Artboard (the white "page") ── */}
        <div className={`
          relative min-h-[80vh] overflow-hidden
          bg-gradient-to-b from-[#0c0c18] to-[#0a0a14]
          border-x border-b border-white/[0.04]
          shadow-[0_40px_100px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.02)]
        `}>
          {/* Column overlay (visible during drag or toggle) */}
          {(showGrid || activeDrop || dragIdx !== null) && <ColumnOverlay showGuides={dragIdx !== null || activeDrop} />}

          {/* Grid canvas */}
          <div
            className="relative z-10 grid grid-cols-12 gap-4 p-6"
            style={{
              minHeight: '60vh',
            }}
          >
            {blocks.length === 0 ? (
              <div
                className={`col-span-12 transition-all duration-300 rounded-2xl border-2 border-dashed ${
                  activeDrop ? 'border-[var(--editor-accent,#3b82f6)] bg-[var(--editor-accent,#3b82f6)]/5 shadow-[0_0_30px_rgba(59,130,246,0.15)]' : 'border-transparent'
                }`}
                onDragOver={(e) => {
                  if (hasCardDragType(e.dataTransfer)) {
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'copy'
                  }
                }}
                onDrop={handleEmptyDrop}
              >
                <EmptyCanvas />
              </div>
            ) : (
              <>
                {/* Blocks grouped into rows with drop zones between rows */}
                {groupBlocksIntoRows(blocks).map((group, rowIdx) => (
                  <div key={`row-${group.startIndex}`} className="contents">
                    {/* Row drop zone before first row */}
                    {rowIdx === 0 && (
                      <RowDropZone
                        index={0}
                        isActive={activeDrop || dragIdx !== null}
                        onDrop={handleInsertDrop}
                        onReorderDrop={handleReorderDrop}
                      />
                    )}

                    {/* Render blocks in this row — they flow side-by-side */}
                    {group.row.map((block) => {
                      const globalIdx = blocks.indexOf(block)
                      return (
                        <BlockWrapper
                          key={block.id}
                          block={block}
                          index={globalIdx}
                          isSelected={selectedBlockId === block.id || (selectedBlockIds?.has(block.id) ?? false)}
                          isMultiSelected={selectedBlockIds ? selectedBlockIds.has(block.id) && selectedBlockIds.size > 1 : false}
                          isLocked={lockedBlockIds?.has(block.id) ?? false}
                          onSelect={(shiftKey) => onSelectBlock(block.id, shiftKey)}
                          onRemove={() => onRemoveBlock(block.id)}
                          onDuplicate={() => onDuplicateBlock(block.id)}
                          onResize={(colSpan) => onResizeBlock(block.id, colSpan)}
                          onUpdateData={(data) => onUpdateBlock(block.id, data)}
                          onContextMenu={(e) => handleBlockContextMenu(e, block.id, globalIdx)}
                          onDragStart={(e) => handleDragStart(e, globalIdx)}
                          onDragEnd={handleDragEnd}
                          onMoveUp={globalIdx > 0 ? () => handleMoveBlock(globalIdx, 'up') : undefined}
                          onMoveDown={globalIdx < blocks.length - 1 ? () => handleMoveBlock(globalIdx, 'down') : undefined}
                          onToggleLock={onToggleBlockLock ? () => onToggleBlockLock(block.id) : undefined}
                          artboardWidth={artboardWidth}
                        />
                      )
                    })}

                    {/* Row drop zone after this row */}
                    <RowDropZone
                      index={group.startIndex + group.row.length}
                      isActive={activeDrop || dragIdx !== null}
                      onDrop={handleInsertDrop}
                      onReorderDrop={handleReorderDrop}
                    />
                  </div>
                ))}
              </>
            )}
          </div>  {/* end grid */}
        </div>  {/* end page background */}
        </div>  {/* end artboard ref */}
      </div>  {/* end height wrapper */}

      {/* ── Status bar at bottom ── */}
      <div className="fixed bottom-0 right-0 left-[var(--sidebar-w,380px)] h-7 bg-[#0d0d1a]/90 backdrop-blur-xl border-t border-white/[0.04] flex items-center justify-between px-4 z-50 text-[9px] font-mono text-white/20">
        <div className="flex items-center gap-4">
          <span>{deviceMode.toUpperCase()} · {artboardWidth}px</span>
          <span className="text-white/10">|</span>
          <span>{blocks.length} block{blocks.length !== 1 ? 's' : ''}</span>
          <span className="text-white/10">|</span>
          <span>12-col grid · 16px gap</span>
        </div>
        <div className="flex items-center gap-4">
          <span>Zoom {Math.round(zoom * 100)}%</span>
          <span className="text-white/10">|</span>
          <span className="text-blue-400/40">⌘S save · ⌘Z undo · ⌘⌫ delete</span>
        </div>
      </div>

      {/* ── Context menu overlay ── */}
      {contextMenu && (
        <BlockContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          blockId={contextMenu.blockId}
          isLocked={lockedBlockIds?.has(contextMenu.blockId) ?? false}
          onClose={() => setContextMenu(null)}
          onDuplicate={() => onDuplicateBlock(contextMenu.blockId)}
          onRemove={() => onRemoveBlock(contextMenu.blockId)}
          onMoveUp={() => handleMoveBlock(contextMenu.blockIdx, 'up')}
          onMoveDown={() => handleMoveBlock(contextMenu.blockIdx, 'down')}
          onToggleLock={() => onToggleBlockLock?.(contextMenu.blockId)}
        />
      )}
    </div>
  )
}
